import React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";

/**
 * Card para preencher variáveis de template WhatsApp ({{1}}, {{2}}…).
 * Mostra o corpo do template e o formato esperado no chat.
 */
export default function AssistantTemplateParamsCard({ artifact, onChoose, onDraft }) {
  const { t } = useTranslation("shell");
  if (!artifact || artifact.kind !== "template_params") return null;

  const body = String(artifact.body || "").trim();
  const slots = Array.isArray(artifact.slots) ? artifact.slots : [];
  const example = String(artifact.exampleReply || artifact.draftReply || "").trim();
  const n = Number(artifact.placeholders) || slots.length;

  const highlightBody = (text) => {
    if (!text) return null;
    const parts = String(text).split(/(\{\{\d+\}\})/g);
    return parts.map((part, i) => {
      if (/^\{\{\d+\}\}$/.test(part)) {
        return (
          <Box
            key={`tok-${i}`}
            component="span"
            sx={{
              display: "inline",
              px: 0.4,
              py: 0.05,
              mx: 0.15,
              borderRadius: 0.75,
              bgcolor: "primary.main",
              color: "primary.contrastText",
              fontWeight: 700,
              fontSize: "0.78em",
            }}
          >
            {part}
          </Box>
        );
      }
      return <React.Fragment key={`txt-${i}`}>{part}</React.Fragment>;
    });
  };

  return (
    <Box
      sx={{
        mt: 0.75,
        p: 1.25,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1.5,
        bgcolor: "background.paper",
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }}>
        {artifact.title ||
          t("platformAi.templateParamsTitle", {
            defaultValue: "Preencha o template",
          })}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
        {artifact.hint ||
          t("platformAi.templateParamsHint", {
            defaultValue:
              "Envie os valores na ordem, separados por | (pipe).",
            count: n,
          })}
      </Typography>

      {body ? (
        <Box
          sx={{
            mb: 1.25,
            p: 1,
            borderRadius: 1,
            bgcolor: "action.hover",
            border: "1px dashed",
            borderColor: "divider",
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 0.5, fontWeight: 600 }}
          >
            {t("platformAi.templateBodyLabel", { defaultValue: "Texto que será enviado" })}
          </Typography>
          <Typography
            variant="body2"
            sx={{ whiteSpace: "pre-wrap", lineHeight: 1.45, wordBreak: "break-word" }}
          >
            {highlightBody(body)}
          </Typography>
        </Box>
      ) : null}

      {slots.length > 0 ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mb: 1.25 }}>
          {slots.map((slot) => (
            <Typography key={slot.index} variant="caption" color="text.secondary">
              <Box component="span" sx={{ fontWeight: 700, color: "text.primary" }}>
                {slot.token}
              </Box>
              {": "}
              {slot.value
                ? slot.value
                : slot.suggested
                  ? t("platformAi.templateSlotSuggested", {
                      defaultValue: "sugestão «{{value}}» — confirme ou troque",
                      value: slot.suggested,
                    })
                  : t("platformAi.templateSlotEmpty", {
                      defaultValue: "ainda sem valor",
                    })}
            </Typography>
          ))}
        </Box>
      ) : null}

      {example ? (
        <Box
          sx={{
            mb: 1,
            p: 1,
            borderRadius: 1,
            bgcolor: "background.default",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.35 }}>
            {t("platformAi.templateExampleLabel", {
              defaultValue: "Envie no chat neste formato",
            })}
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", wordBreak: "break-word" }}
          >
            {example}
          </Typography>
        </Box>
      ) : null}

      {example && typeof onDraft === "function" ? (
        <Button
          size="small"
          variant="outlined"
          onClick={() => onDraft(example)}
          sx={{ textTransform: "none" }}
        >
          {t("platformAi.templateUseDraft", {
            defaultValue: "Colocar formato no chat (edite os …)",
          })}
        </Button>
      ) : example && typeof onChoose === "function" ? (
        <Button
          size="small"
          variant="outlined"
          onClick={() => onChoose(example)}
          sx={{ textTransform: "none" }}
        >
          {t("platformAi.templateUseDraft", {
            defaultValue: "Colocar formato no chat (edite os …)",
          })}
        </Button>
      ) : null}
    </Box>
  );
}
